import {
    Box, Button, IconButton, InputBase, List, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem,
    Typography
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import Block from "@mui/icons-material/Block";
import Check from "@mui/icons-material/Check";
import Event from "@mui/icons-material/Event";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import Person from "@mui/icons-material/Person";
import RepeatIcon from "@mui/icons-material/Repeat";
import moment, { Moment } from "moment";
import { useAsyncAction } from "nate-react-api-helpers";
import { useRef, useState } from "react";
import { api } from "../../../api/API";
import { Attachment, emptyRepeat, Task, TaskInput, TaskLink } from "../../../api/Tasks";
import { dateFormat } from "../../../misc/dates";
import { useCurrentUser } from "../../../misc/CurrentUser";
import { HtmlEditor } from "../../../misc/HtmlEditor";
import { useSyncedRef } from "../../../misc/SyncedRef";
import { useAutoSave } from "../../../misc/useAutoSave";
import { isPersonalTeam, teamName, useWorkspace } from "../../../misc/Workspace";
import { ProjectCode } from "../../project/ProjectCode";
import { DueDateCalendarHeader } from "../panel/DueDateCalendarHeader";
import { ProjectRef } from "../panel/TaskProject";
import { TaskChecklist } from "../panel/TaskChecklist";
import { ChecklistProgress } from "../panel/ChecklistProgress";
import { useMoveTask } from "../panel/useMoveTask";
import { describeRepeat } from "../repeatText";
import { useCompleteTask, vibrate } from "../useCompleteTask";
import { BottomSheet } from "./BottomSheet";
import { CompleteButton } from "./CompleteButton";
import { MobileAssignees } from "./MobileAssignees";
import { MobileAttachments } from "./MobileAttachments";
import { MobileFieldRow } from "./MobileFieldRow";
import { MobileProjectSheet } from "./MobileProjectSheet";
import { MobileRepeatField, RepeatSheet } from "./MobileRepeatField";
import { gutter, MobileCard, MobileSheet } from "./MobileSheet";

// MobileTaskEditor is the whole of a task on one compact sheet: title, completion, due
// date and repeat, project, assignees, what it waits on, description, attachments and
// checklist. Comments and editing links are left to the desktop panel (see TaskPanel).
export function MobileTaskEditor(props: {
    value: Partial<Task>;
    onClose: () => void;

    // onSaved reports the saved task, so the page can keep showing it
    onSaved: (task: Task) => void;
}) {
    const me = useCurrentUser();
    const ws = useWorkspace();
    const isNew = !props.value.id;

    // the parts of a task this editor doesn't change ride along on every save. The
    // editor is re-keyed per task, so what it opened on is what it passes back.
    const opened = useRef(props.value);
    const isItem = opened.current.type === "checklist";
    const team = opened.current.team || ws.newTeam;

    const [title, setTitle] = useState(props.value.title || "");
    const [description, setDescription] = useState(props.value.description || "");
    const [writing, setWriting] = useState(!!props.value.description);
    const [dueDate, setDueDate] = useState(props.value.dueDate || "");
    const [repeat, setRepeat] = useState(props.value.repeat || emptyRepeat);
    const [completedAt, setCompletedAt] = useState(props.value.completedAt || null);
    const [attachments, setAttachments] = useState<Attachment[]>(props.value.attachments || []);
    const [checklist, setChecklist] = useState<TaskLink[]>(props.value.checklist || []);
    const [assigned, setAssigned] = useState<number[]>(() => {
        if (!isNew) return (props.value.assignedTo || []).map(a => a.usr);
        return me ? [me.id] : [];
    });

    const [project, setProject] = useState<ProjectRef | null>(props.value.project ? {
        id: props.value.project,
        name: props.value.projectName || "",
        color: props.value.projectColor || "",
        goal: props.value.goal || 0,
        goalName: props.value.goalName || "",
    } : null);
    const [projectCode, setProjectCode] = useState(props.value.projectCode || "");

    const [menu, setMenu] = useState<HTMLElement | null>(null);
    const [sheet, setSheet] = useState<"project" | "team" | "due" | null>(null);

    // the repeat opens from the due row's button, as well as from inside the due sheet
    const [repeatOpen, setRepeatOpen] = useState(false);

    const input: TaskInput = {
        id: props.value.id,
        type: opened.current.type || "task",
        parent: opened.current.parent || null,
        title: title,
        description: description,
        dueDate: dueDate || null,
        dueTime: opened.current.dueTime || null,
        completedAt: completedAt,
        project: project ? project.id : null,
        goal: project && project.goal ? project.goal : null,
        team: team,
        archived: opened.current.archived || false,
        repeat: repeat,
        assignedTo: assigned.map(usr => ({ usr: usr })),
    };

    const inputRef = useSyncedRef(input);
    const onSavedRef = useSyncedRef(props.onSaved);

    // version is the copy of the task this editor last saw, so the server can turn away
    // a save that would undo a change made somewhere else
    const version = useRef(props.value.updatedAt);

    const save = async (posted: TaskInput) => {
        const result = await api.tasks.upsert({ ...posted, updatedAt: version.current });
        version.current = result.updatedAt;
        setProjectCode(result.projectCode);
        onSavedRef.current(result);
    };

    // a task with no title yet is one the server would turn away, so it waits for one
    const autoSave = useAutoSave({
        input: input,
        off: isNew || !title.trim(),
        text: [title, description],
        fields: [dueDate, repeat, project, assigned],
        save: save,
    });

    // the tick moves as it's tapped so it answers straight away, and goes back if the
    // call fails (see useCompleteTask)
    const complete = useCompleteTask();
    const toggle = () => {
        if (complete.loading) return;

        const was = completedAt;
        const done = !completedAt;

        vibrate();
        setCompletedAt(done ? moment().toISOString() : null);
        complete.callback({
            id: props.value.id || 0,
            completed: done,
            onFail: () => setCompletedAt(was),
            onUndone: () => setCompletedAt(null),
            onSaved: task => version.current = task.updatedAt,
        });
    };

    // a task that doesn't exist yet has nothing to save into, so it's created here. The
    // images added before the save are linked to it after.
    const create = useAsyncAction(async () => {
        const result = await api.tasks.upsert(inputRef.current);
        for (const file of attachments) {
            await api.tasks.setFile({ task: result.id, file: file.id, archived: false });
        }

        onSavedRef.current(result);
        props.onClose();
    }, [attachments]);

    const archive = useAsyncAction(async () => {
        await api.tasks.upsert({ ...inputRef.current, archived: true, updatedAt: version.current });
        props.onClose();
    }, []);

    // a moved task can't be shown as it was, so the sheet closes on it
    const move = useMoveTask({ onMoved: props.onClose });

    const busy = create.loading || archive.loading || move.loading;
    const error = autoSave.error || create.error || archive.error;

    let status = "";
    if (busy || autoSave.loading) status = "Saving...";
    else if (error) status = error;
    else if (autoSave.saved) status = "Saved";

    const blockedBy = props.value.blockedBy || [];

    return (
        <MobileSheet
            onClose={props.onClose}
            title={<>
                {!isNew && (
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>#{props.value.id}</Typography>
                )}

                {/* what the editor has done with the edits so far, in place of the
                    save button an existing task doesn't need */}
                <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0, opacity: error ? 1 : 0.75 }}>
                    {status}
                </Typography>
            </>}
            actions={isNew ? (
                // white on the blue bar, and still readable while it waits for a title
                <Button variant="contained" disableElevation disabled={busy || !title.trim()}
                        onClick={() => create.callback(null)}
                        sx={{
                            bgcolor: "common.white",
                            color: "primary.main",
                            "&:hover": { bgcolor: "grey.100" },
                            "&.Mui-disabled": { bgcolor: "rgba(255, 255, 255, 0.35)", color: "rgba(255, 255, 255, 0.85)" },
                        }}>
                    Save
                </Button>
            ) : (
                <>
                    <IconButton color="inherit" aria-label="More" disabled={busy}
                                onClick={e => setMenu(e.currentTarget)}>
                        <MoreHoriz />
                    </IconButton>
                    <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
                        {!isItem && (
                            <MenuItem onClick={() => {
                                setMenu(null);
                                setSheet("team");
                            }}>
                                Move to team
                            </MenuItem>
                        )}
                        <MenuItem onClick={() => {
                            setMenu(null);
                            archive.callback(null);
                        }}>
                            Archive
                        </MenuItem>
                    </Menu>
                </>
            )}
        >
            <MobileCard inset>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <InputBase
                        autoFocus={isNew}
                        fullWidth
                        multiline
                        placeholder="Task title"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        sx={{ typography: "h6", flex: 1, py: 0.5 }}
                    />
                    {!isNew && (
                        <CompleteButton done={!!completedAt} disabled={complete.loading} onClick={toggle} />
                    )}
                </Box>
            </MobileCard>

            <MobileCard>
                <MobileFieldRow icon={<Event fontSize="small" />} onClick={() => setSheet("due")}
                                action={!isItem && (
                                    <IconButton aria-label="Repeat" onClick={() => setRepeatOpen(true)}>
                                        <RepeatIcon fontSize="small"
                                                    sx={{ color: repeat.kind ? "success.main" : "action.active" }} />
                                    </IconButton>
                                )}>
                    <Typography variant="body2" color={dueDate ? "text.primary" : "text.secondary"}>
                        {dueDate ? moment(dueDate).format("ddd, MMM D, YYYY") : "No due date"}
                    </Typography>
                    {!isItem && !!describeRepeat(repeat) && (
                        <Typography variant="caption" color="text.secondary">{describeRepeat(repeat)}</Typography>
                    )}
                </MobileFieldRow>

                {!isItem && (
                    <MobileFieldRow icon={<FolderOutlined fontSize="small" />} onClick={() => setSheet("project")}>
                        {project ? (
                            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
                                <ProjectCode code={projectCode} color={project.color} />
                                <Typography variant="body2" noWrap>
                                    {project.name}{project.goal ? " › " + project.goalName : ""}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">No project</Typography>
                        )}
                    </MobileFieldRow>
                )}

                {/* on a personal team the task can only be the user's */}
                {!isPersonalTeam(ws.teams, team) && (
                    <MobileFieldRow icon={<Person fontSize="small" />}>
                        <MobileAssignees team={team} value={assigned} onChange={setAssigned} />
                    </MobileFieldRow>
                )}

                {blockedBy.length > 0 && (
                    <MobileFieldRow icon={<Block fontSize="small" />}>
                        {blockedBy.map(link => (
                            <Box key={link.id} sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                                <Typography variant="body2" noWrap
                                            color={link.noAccess || link.completedAt ? "text.secondary" : "text.primary"}>
                                    {link.noAccess ? "No access to task #" + link.id : "#" + link.id + " " + link.title}
                                </Typography>
                                {link.completedAt && <Check fontSize="small" color="success" />}
                            </Box>
                        ))}
                    </MobileFieldRow>
                )}
            </MobileCard>

            <MobileCard>
                {writing ? (
                    // full-bleed: the rules the editor draws run the width of the screen,
                    // and its own inset keeps the text in line with the fields above
                    <HtmlEditor minHeight={120} inset={gutter} autoFocus={!props.value.description}
                                value={description} onChange={setDescription} />
                ) : (
                    <Box sx={{ px: `${gutter}px`, py: 1 }}>
                        <Button size="small" onClick={() => setWriting(true)}>Add description</Button>
                    </Box>
                )}
            </MobileCard>

            <MobileCard>
                <Typography variant="caption" color="text.secondary" component="div"
                            sx={{ px: `${gutter}px`, pt: 1 }}>
                    Attachments
                </Typography>
                <MobileAttachments taskId={props.value.id || 0} value={attachments} onChange={setAttachments} />
            </MobileCard>

            {!isNew && !isItem && (
                <MobileCard>
                    <Typography variant="caption" color="text.secondary" component="div"
                                sx={{ px: `${gutter}px`, pt: 1 }}>
                        Checklist
                    </Typography>
                    {/* the checklist rows hold themselves off the edges like a panel does */}
                    <Box sx={{ px: 3 }}>
                        <ChecklistProgress value={checklist} sx={{ mt: "4px" }} />
                        <TaskChecklist taskId={props.value.id || 0} value={checklist} onChange={setChecklist}
                                       completedAt={completedAt} dueDate={dueDate || null} />
                    </Box>
                </MobileCard>
            )}

            <BottomSheet open={sheet === "due"} title="Due" onClose={() => setSheet(null)}>
                {/* a day is picked straight off the month, and the sheet goes with the pick */}
                <DateCalendar
                    value={dueDate ? moment(dueDate) : null}
                    onChange={(value: Moment | null) => {
                        if (!value) return;
                        setDueDate(value.format(dateFormat));
                        setSheet(null);
                    }}
                    slots={{ calendarHeader: DueDateCalendarHeader }}
                    sx={{ mx: "auto" }}
                />

                {!isItem && (
                    <MobileRepeatField value={repeat} dueDate={dueDate || null} onChange={setRepeat} />
                )}

                <Box sx={{ display: "flex", justifyContent: "space-between", px: 1, pt: 1, pb: 1 }}>
                    {/* a repeat steps on from the due date, so it goes with it */}
                    <Button color="inherit" disabled={!dueDate && !repeat.kind} onClick={() => {
                        setDueDate("");
                        setRepeat(emptyRepeat);
                        setSheet(null);
                    }}>
                        Clear date
                    </Button>
                    <Button onClick={() => setSheet(null)}>Done</Button>
                </Box>
            </BottomSheet>

            {/* mounted per opening, so the page starts from what the task carries now */}
            {repeatOpen && (
                <RepeatSheet
                    value={repeat}
                    dueDate={dueDate || null}
                    onClose={next => {
                        setRepeatOpen(false);
                        setRepeat(next);
                    }}
                />
            )}

            <MobileProjectSheet
                open={sheet === "project"}
                team={team}
                onClose={() => setSheet(null)}
                onChange={value => {
                    setProject(value);
                    if (!value) setProjectCode("");
                }}
            />

            <BottomSheet open={sheet === "team"} title="Move to team" onClose={() => setSheet(null)}>
                <List disablePadding>
                    {ws.teams.map(row => (
                        <ListItemButton key={row.id} selected={row.id === team} onClick={() => {
                            setSheet(null);
                            if (row.id !== team) move.start({ task: props.value as Task, team: row.id });
                        }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>{row.id === team && <Check fontSize="small" />}</ListItemIcon>
                            <ListItemText primary={teamName(row, row.id)} />
                        </ListItemButton>
                    ))}
                </List>
            </BottomSheet>

            {move.element}
        </MobileSheet>
    );
}
